import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';
import TokenExpirationAlert from '../components/TokenExpirationAlert';

const AuthContext = createContext(null);

const WARNING_THRESHOLD = 5 * 60 * 1000; // 5 minutes in milliseconds

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem('wmf_token'));
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showExpirationWarning, setShowExpirationWarning] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const [expirationTimer, setExpirationTimer] = useState(null);
  const [warningTimer, setWarningTimer] = useState(null);

  const clearTimers = () => {
    if (expirationTimer) clearTimeout(expirationTimer);
    if (warningTimer) clearTimeout(warningTimer);
  };

  const setExpirationTimers = useCallback((expirationTime) => {
    clearTimers();

    const now = Date.now();
    const timeUntilExpiration = expirationTime * 1000 - now;
    const timeUntilWarning = timeUntilExpiration - WARNING_THRESHOLD;

    // Set warning timer
    if (timeUntilWarning > 0) {
      const warningTimerId = setTimeout(() => {
        setShowExpirationWarning(true);
        setRemainingTime(WARNING_THRESHOLD);

        // Update remaining time every second
        const interval = setInterval(() => {
          setRemainingTime(prev => {
            if (prev <= 1000) {
              clearInterval(interval);
              return 0;
            }
            return prev - 1000;
          });
        }, 1000);

        // Clear interval when warning is dismissed
        setWarningTimer(interval);
      }, timeUntilWarning);

      setWarningTimer(warningTimerId);
    }

    // Set expiration timer
    if (timeUntilExpiration > 0) {
      const expirationTimerId = setTimeout(() => {
        logout();
      }, timeUntilExpiration);

      setExpirationTimer(expirationTimerId);
    }
  }, []);

  const renewToken = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/renew`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to renew token');
      }

      const { token: newToken } = await response.json();
      const decoded = jwtDecode(newToken);

      login(newToken, decoded);
      setShowExpirationWarning(false);
      clearTimers();
      setExpirationTimers(decoded.exp);

      return true;
    } catch (error) {
      console.error('Token renewal failed:', error);
      return false;
    }
  };

  const initializeAuth = useCallback(async () => {
    try {
      if (token) {
        const decoded = jwtDecode(token);
        
        // Check if token has expired
        if (decoded.exp * 1000 < Date.now()) {
          logout();
        } else {
          setUser({
            email: decoded.sub,
            role: decoded.role,
            firstName: decoded.firstName,
            lastName: decoded.lastName,
            userId: decoded.userId
          });
          setExpirationTimers(decoded.exp);
        }
      }
    } catch (error) {
      console.error('Auth initialization error:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [token, setExpirationTimers]);

  useEffect(() => {
    initializeAuth();

    return () => clearTimers();
  }, [initializeAuth]);

  // A sign-in completed in another tab (e.g. while this tab kept an expired
  // assessment open) is picked up here instead of requiring a reload.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'wmf_token') setToken(e.newValue);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const login = (newToken, decodedToken) => {
    localStorage.setItem('wmf_token', newToken);
    setToken(newToken);
    setUser({
      email: decodedToken.sub,
      role: decodedToken.role,
      firstName: decodedToken.firstName,
      lastName: decodedToken.lastName,
      userId: decodedToken.userId
    });
    setExpirationTimers(decodedToken.exp);
  };

  const logout = () => {
    localStorage.removeItem('wmf_token');
    setToken(null);
    setUser(null);
    clearTimers();
    setShowExpirationWarning(false);
  };

  const isAuthenticated = () => {
    if (!token || !user) return false;
    
    try {
      const decoded = jwtDecode(token);
      return decoded.exp * 1000 > Date.now();
    } catch {
      return false;
    }
  };

  const hasRole = (requiredRole) => {
    return user?.role === requiredRole;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ 
      token, 
      user, 
      login, 
      logout, 
      isAuthenticated, 
      hasRole,
      loading
    }}>
      {children}
      {showExpirationWarning && (
        <TokenExpirationAlert
          onRenew={renewToken}
          onClose={() => setShowExpirationWarning(false)}
          remainingTime={remainingTime}
        />
      )}
    </AuthContext.Provider>
  );
};

const logout = async () => {
  try {
    clearTimers(); // Clear any existing timers
    setShowExpirationWarning(false); // Hide expiration warning if showing
    localStorage.removeItem('wmf_token');
    setToken(null);
    setUser(null);
    
    // Optionally notify the backend about the logout
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/auth/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
    } catch (error) {
      // If the backend call fails, we still want to complete the local logout
      console.error('Error notifying backend of logout:', error);
    }

  } catch (error) {
    console.error('Logout error:', error);
    throw error;
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
